// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SelectSectionCard from 'components/molecules/SelectSectionCard';
import SelectFloorCard from 'components/molecules/SelectFloorCard';
import { LoadingModal } from 'components/molecules/LoadingModal';
import { PrimaryTemplate } from 'components/templates/PrimaryTemplate';
import {
  isTenantInfoLoaded,
  selectSectionsWithNoTenant,
  selectSectionsWithTenants,
  selectTenants,
  selectTenantsWithNoSection,
  setFloors,
  setForceReloadSection,
  setSections,
  setTenants,
} from 'redux/slices/siteInfoSlice';
import {
  selectLoading,
  setOfflineInitialized,
} from 'redux/slices/loadingSlice';
import { selectUser } from 'redux/slices/userSlice';
import { Path } from 'routes';
import { createAxios } from 'ts/createAxios';
import { DbManager } from 'util/DbManager';
import { handleError } from 'util/errorHandler';
import { NETWORK_ERROR_MESSAGE } from 'util/ConstantValues';
import { updateTenantsSortIndexes } from 'apis/tenants';
import styles from './TenantSort.module.scss';
import { mergeClasses, sortTenants } from 'util/commons';
import Portal from 'components/atoms/Portal';
import EmissionSuccessModal from 'components/molecules/EmissionSuccessModal';
import { useHistory } from 'react-router';
import EmissionConfirmLeavePageModal from 'components/molecules/EmissionConfirmLeavePageModal';
import { useQuery } from 'util/queryParams';
import { Section, Tenant, Floor } from 'util/Types';

type FetchSectionsRes = {
  sections: Section[];
  operatorName: string;
  siteName: string;
};
type FetchTenantsRes = {
  tenants: Tenant[];
  operatorName: string;
  siteName: string;
};
type FetchFloorsRes = {
  floors: Floor[];
  operatorName: string;
  siteName: string;
};

const SortableTenantCard: React.FC<{ tenant: Tenant }> = ({ tenant }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tenant.id.toString() });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 999 : undefined,
    opacity: isDragging ? 0.85 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`${styles.tenantItem} ${isDragging ? styles.dragging : ''}`}
    >
      <div className={styles.dragDots}>
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="4" cy="2" r="1.5" fill="#9f9f9f" />
          <circle cx="10" cy="2" r="1.5" fill="#9f9f9f" />
          <circle cx="4" cy="7" r="1.5" fill="#9f9f9f" />
          <circle cx="10" cy="7" r="1.5" fill="#9f9f9f" />
          <circle cx="4" cy="12" r="1.5" fill="#9f9f9f" />
          <circle cx="10" cy="12" r="1.5" fill="#9f9f9f" />
        </svg>
      </div>
      <p title={tenant.name}>{tenant.name}</p>
    </div>
  );
};

const TenantSort: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const api = createAxios();
  const user = useSelector(selectUser);
  const { offlineInitialized } = useSelector(selectLoading);
  const isLoaded = useSelector(isTenantInfoLoaded);
  const history = useHistory();
  const searchParams = useQuery();

  const sections = useSelector(selectSectionsWithTenants);
  const oSections = useSelector(selectSectionsWithNoTenant);
  const oTenants = useSelector(selectTenantsWithNoSection);
  const allTenants = useSelector(selectTenants);

  const [selectedSection, setSelectedSection] = useState<Section | undefined>();
  const [selectedFloor, setSelectedFloor] = useState<Floor | undefined>();
  const [sortedTenants, setSortedTenants] = useState<Tenant[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSavedModal, setShowSavedModal] = useState(false);
  const [showLeavePageModal, setShowLeavePageModal] = useState(false);
  const initialized = useRef(false);
  const pendingTransitionRef = useRef<(() => void) | null>(null);

  const currentTenants = useMemo<Tenant[]>(() => {
    const sort = (list: Tenant[]) => [...list].sort(sortTenants);

    if (sections.length > 0) {
      if (selectedFloor) {
        return sort(allTenants.filter((t) => t.floor?.id === selectedFloor.id));
      }

      if (selectedSection) {
        return sort(
          allTenants.filter(
            (t) => t.section?.id === selectedSection.id && !t.floor
          )
        );
      }
      return [];
    }

    if (oSections.length > 0 && selectedSection) {
      return sort(
        allTenants.filter((t) => t.section?.id === selectedSection.id)
      );
    }

    return sort(oTenants);
  }, [
    sections,
    oSections,
    selectedSection,
    selectedFloor,
    oTenants,
    allTenants,
  ]);

  useEffect(() => {
    if (isSaving) return;
    setSortedTenants(currentTenants);
    setIsDirty(false);
  }, [currentTenants, isSaving]);

  const fetchData = async (force = false) => {
    if (force || !isLoaded) {
      if (!offlineInitialized) return;

      const fetchLocalData = async () => {
        const cachedSites = await DbManager.sites.toArray();
        if (cachedSites.length) {
          const [cachedSections, cachedTenants, cachedFloors] =
            await Promise.all([
              DbManager.sections.orderBy('name').toArray(),
              DbManager.tenants.orderBy('name').toArray(),
              DbManager.floors.orderBy('name').toArray(),
            ]);
          dispatch(setSections(cachedSections));
          dispatch(setTenants(cachedTenants));
          dispatch(setFloors(cachedFloors));
          return true;
        }
        return false;
      };

      if (!force) {
        const isLocalDataFetched = await fetchLocalData();
        if (isLocalDataFetched) return;
      }

      Promise.all([
        api.get<FetchSectionsRes>(`/sections?siteId=${user.siteId}`),
        api.get<FetchFloorsRes>(`/floors?siteId=${user.siteId}`),
        api.get<FetchTenantsRes>(`/tenants?siteId=${user.siteId}`),
      ])
        .then(async (response) => {
          dispatch(setSections(response[0].data.sections));
          dispatch(setTenants(response[2].data.tenants));
          dispatch(setFloors(response[1].data.floors));
          dispatch(setForceReloadSection(true));

          await Promise.all([
            DbManager.sections.clear(),
            DbManager.tenants.clear(),
            DbManager.floors.clear(),
          ]);
          await Promise.all([
            DbManager.sections.bulkAdd(response[0].data.sections),
            DbManager.tenants.bulkAdd(response[2].data.tenants),
            DbManager.floors.bulkAdd(response[1].data.floors),
          ]);
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch(async (e: any) => {
          if (e.message === NETWORK_ERROR_MESSAGE) {
            await fetchLocalData();
          } else {
            handleError(e);
          }
        });
    }
  };

  useEffect(() => {
    if (user && offlineInitialized) {
      fetchData();
    }
  }, [user, offlineInitialized]);

  useEffect(() => {
    if (!isLoaded || initialized.current) return;

    const allSections = (sections.length ? sections : oSections) as Section[];
    const sectionIdFromParams = searchParams.get('sectionId');
    const firstSection = sectionIdFromParams
      ? allSections.find((s) => s.id.toString() === sectionIdFromParams)
      : allSections[0];

    setSelectedSection(firstSection);

    if (sections.length && firstSection?.floors?.length) {
      const floorIdFromParams = searchParams.get('floorId');
      const firstFloor = floorIdFromParams
        ? firstSection?.floors?.find(
            (f) => f.id.toString() === floorIdFromParams
          )
        : firstSection?.floors?.[0];
      setSelectedFloor(firstFloor);
    }

    initialized.current = true;
  }, [isLoaded]);

  const handleSelectSection = (section: Section) => {
    setSelectedSection(section);

    const search = new URLSearchParams(history.location.search);
    search.set('sectionId', section.id.toString());
    search.delete('floorId');

    if (sections.length && section.floors?.length) {
      const firstFloor = section.floors[0];
      setSelectedFloor(firstFloor);
      search.set('floorId', firstFloor.id.toString());
    } else {
      setSelectedFloor(undefined);
    }

    history.replace({
      pathname: history.location.pathname,
      search: search.toString(),
    });
  };

  const handleSelectFloor = (floor: Floor) => {
    setSelectedFloor(floor);

    const search = new URLSearchParams(history.location.search);
    search.set('floorId', floor.id.toString());
    history.replace({
      pathname: history.location.pathname,
      search: search.toString(),
    });
  };

  const handlePrev = () => {
    const url = `${Path.selectTenant}${window.location.search}`;

    if (isDirty) {
      pendingTransitionRef.current = () => history.push(url);
      setShowLeavePageModal(true);
    } else {
      history.push(url);
    }
  };

  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (isDirty) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setSortedTenants((items) => {
      const oldIndex = items.findIndex((t) => t.id.toString() === active.id);
      const newIndex = items.findIndex((t) => t.id.toString() === over.id);
      return arrayMove(items, oldIndex, newIndex);
    });

    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!isDirty) return;

    setIsSaving(true);
    const payload = sortedTenants.map((t, index) => ({
      id: t.id,
      sortIndex: index + 1,
    }));

    await updateTenantsSortIndexes({
      tenants: payload,
      successCallback: () => {
        const updatedTenants = allTenants.map((t) => {
          const found = payload.find((p) => p.id === t.id);

          return found ? { ...t, sortIndex: found.sortIndex } : t;
        });

        dispatch(setTenants(updatedTenants));
        setShowSavedModal(true);
        setIsDirty(false);
        setIsSaving(false);
      },
    });

    setIsSaving(false);
  };

  if (!offlineInitialized) {
    return (
      <LoadingModal
        isOpen={!offlineInitialized}
        closeAction={{ onClose: () => dispatch(setOfflineInitialized(true)) }}
      />
    );
  }

  const displaySections = sections.length ? sections : oSections;

  return (
    <PrimaryTemplate hasLogo prevFunction={handlePrev}>
      <div className={styles.container}>
        <div className={styles.body}>
          <p className={styles.hint}>{t('select_tenant.sort.note')}</p>

          {displaySections.length > 0 && (
            <div className={styles.sectionList}>
              {displaySections.map((s) => (
                <SelectSectionCard
                  key={s.id}
                  section={s}
                  isSelected={s.id === selectedSection?.id}
                  onSelectSection={handleSelectSection}
                />
              ))}
            </div>
          )}

          {sections.length > 0 &&
            selectedSection?.floors &&
            selectedSection.floors.length > 0 && (
              <div className={styles.floorList}>
                {selectedSection.floors.map((f) => (
                  <SelectFloorCard
                    key={f.id}
                    floor={f}
                    isSelected={f.id === selectedFloor?.id}
                    onSelectFloor={handleSelectFloor}
                  />
                ))}
              </div>
            )}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortedTenants.map((t) => t.id.toString())}
              strategy={rectSortingStrategy}
            >
              <div className={styles.tenantWrapper}>
                <div className={styles.tenantList}>
                  {sortedTenants.map((tenant) => (
                    <SortableTenantCard key={tenant.id} tenant={tenant} />
                  ))}
                </div>
              </div>
            </SortableContext>
          </DndContext>
        </div>

        <div className={styles.saveWrapper}>
          <button
            className={mergeClasses(styles.saveButton, {
              [styles.saveButtonDisabled]: !isDirty,
            })}
            onClick={handleSave}
            disabled={!isDirty}
          >
            {t('common.button.keep')}
          </button>
        </div>
      </div>

      {showSavedModal && (
        <Portal>
          <EmissionSuccessModal
            onSubmit={() => history.push(Path.selectTenant)}
          />
        </Portal>
      )}

      {showLeavePageModal && isDirty && (
        <Portal>
          <EmissionConfirmLeavePageModal
            onClose={() => setShowLeavePageModal(false)}
            onSubmit={() => {
              setShowLeavePageModal(false);
              setIsDirty(false);
              pendingTransitionRef.current?.();
              pendingTransitionRef.current = null;
            }}
          />
        </Portal>
      )}
    </PrimaryTemplate>
  );
};

export default TenantSort;
